import { IconButton, styled, ImageListItem, ImageList, Box } from '@mui/material'
import React, { useMemo, useState } from 'react'
import ImageWithPlaceholder from './ImageWithPlaceholder'
import PlaceholderImage from '../assets/images/image-placeholder.png'
import MaterialSymbol from './MaterialSymbol'
import { colors } from '../themes/colors'
import Typography from './Typography'

const MainContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  gap: '32px',
  [theme.breakpoints.down('lg')]: {
    gap: '24px',
  },
}))

const SlideContainer = styled('div')<{ adjust?: boolean }>(({ adjust }) => ({
  display: 'flex',
  alignItems: 'center',
  height: `calc(min(70vw, 400px) ${adjust ? ' + 48px' : ''})`,
}))

const ImageContainer = styled('div')<{ adjust?: boolean }>(({ theme, adjust }) => ({
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  gap: '24px',
  width: '400px',
  [theme.breakpoints.down('sm')]: {
    width: `calc(min(65vw, 400px))`,
    height: `calc(min(65vw, 400px) ${adjust ? ' + 24px' : ''})`,
  },
}))

const Image = styled(ImageWithPlaceholder)(({ theme }) => ({
  display: 'flex',
  animation: 'slide 5s infinite',
  [theme.breakpoints.down('sm')]: {
    width: `calc(min(60vw, 400px))`,
    height: `calc(min(60vw, 400px))`,
  },
}))

const ImageSmall = styled('img')(({ theme }) => ({
  gap: '16px',
  width: '80px',
  height: '80px',
  [theme.breakpoints.down('sm')]: {
    width: '56px',
    height: '56px',
  },
}))

interface ArrowProps {
  icon: string
  onClick: () => void
}

const Arrow = ({ icon, onClick }: ArrowProps) => (
  <IconButton aria-label={icon} color='primary' onClick={onClick} sx={{ p: 0 }}>
    <MaterialSymbol sx={{ fontSize: 40 }}>{icon}</MaterialSymbol>
  </IconButton>
)

interface Props {
  images: string[]
}

export const ImageSlider = ({ images }: Props) => {
  const [currentImage, setCurrentImage] = useState<string>(images[0])
  const [imageIdx, setImageIdx] = useState<number>(0)
  const [validImages, setValidImages] = useState<string[]>([])

  const isMultiImages = React.useMemo(() => validImages.length > 1, [validImages])
  const [noImageText, setNoImageText] = useState<string>()

  const handleLeftArrow = () => {
    if (imageIdx === 0) {
      setImageIdx(validImages.length - 1)
      setCurrentImage(validImages[validImages.length - 1])
    } else {
      setImageIdx(imageIdx - 1)
      setCurrentImage(validImages[imageIdx - 1])
    }
  }

  const handleRightArrow = () => {
    if (imageIdx === validImages.length - 1) {
      setImageIdx(0)
      setCurrentImage(validImages[0])
    } else {
      setImageIdx(imageIdx + 1)
      setCurrentImage(validImages[imageIdx + 1])
    }
  }

  const handleClickOnImage = (event: React.MouseEvent<HTMLElement>, index: number) => {
    setImageIdx(index)
    setCurrentImage(validImages[imageIdx + 1])
  }

  const fetchData = async () => {
    try {
      const data = await Promise.all(
        images.map((image) => fetch(image).then((res) => (res.ok ? res.url : undefined))),
      )

      if (data.filter((x) => x === undefined).length === images.length)
        setNoImageText('No images are available for this product')
      else setValidImages(data.filter((x) => x !== undefined) as string[])
    } catch (err) {
      console.log('error', err)
    }
  }

  React.useEffect(() => {
    if (images.length > 0) {
      fetchData()
    }
  }, [images])

  const renderMainImage = useMemo(() => {
    return (
      <ImageContainer adjust={!!noImageText}>
        <Image
          src={images.length === 0 ? '' : currentImage}
          placeholderImageUrl={PlaceholderImage}
          alt=''
        />
        {noImageText && <Typography variant='body2' textAlign="center">{noImageText}</Typography>}
      </ImageContainer>
    )
  }, [images, noImageText, currentImage, PlaceholderImage])

  return (
    <MainContainer>
      <SlideContainer
        adjust={!!noImageText}
        sx={{ justifyContent: isMultiImages ? 'space-between' : 'center' }}
      >
        {isMultiImages && <Arrow icon='chevron_left' onClick={handleLeftArrow} />}
        {renderMainImage}
        {isMultiImages && <Arrow icon='chevron_right' onClick={handleRightArrow} />}
      </SlideContainer>
      {isMultiImages && (
        <Box sx={{ width: '100%', display: 'flex' }}>
          <ImageList cols={5} gap={16} sx={{ m: 0 }}>
            {validImages.map((item, index) => (
              <ImageListItem
                key={item}
                component={'button'}
                onClick={(e) => handleClickOnImage(e, index)}
                sx={{
                  border: `1px solid ${index === imageIdx ? colors.primary500 : 'transparent'}`,
                  p: 0,
                  transition: `all 1s ease`,
                }}
              >
                <ImageSmall
                  src={item}
                  alt={''}
                  loading='lazy'
                  onError={(e) => (e.currentTarget.style.display = 'none')}
                />
              </ImageListItem>
            ))}
          </ImageList>
        </Box>
      )}
    </MainContainer>
  )
}
